import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, Icon, Label, Select, Table, Text, TextInput } from '@gravity-ui/uikit';
import { Magnifier } from '@gravity-ui/icons';
import type { AdminTenantSummary, SubscriptionState } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { day } from '../../ui/format.js';
import { Page } from '../../ui/page.js';
import { RouterLink, useGo } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { KIND_LABEL, STATE_LABEL, STATE_TONE } from './labels.js';

/**
 * Клиники и лаборатории платформы — таблица консоли с отбором над ней.
 *
 * Область — ровно то, что показывает таблица: название, тип, состояние
 * подписки, контакт владельца, менеджер, дата регистрации. Ни визитов, ни
 * пациентов, ни выручки клиники здесь нет и не будет: платформенный оператор
 * не должен видеть больше, чем нужно для работы с подпиской
 * (docs/03-modules/20-platform-admin.md §1, ADR-0011).
 *
 * Отбор живёт в адресной строке: обзор ссылается на «активных клиник» и
 * «клиники без менеджера» готовым адресом.
 */

const routeApi = getRouteApi('/shell/tenants');
const PAGE_SIZE = 50;
const ANY = '__any';

export function TenantsListScreen() {
  const api = useTenantsApi();
  const managersApi = useManagersApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const go = useGo();

  // Поле поиска — своё состояние; в адрес уходит по Enter или при уходе с
  // поля, а не на каждую букву.
  const [qDraft, setQDraft] = useState(search.q ?? '');

  const query = useInfiniteQuery({
    queryKey: ['tenants', search.q, search.state, search.kind, search.managerId],
    queryFn: ({ pageParam }) =>
      api.list({
        limit: PAGE_SIZE,
        cursor: pageParam,
        ...(search.q === undefined ? {} : { q: search.q }),
        ...(search.state === undefined ? {} : { state: search.state }),
        ...(search.kind === undefined ? {} : { kind: search.kind }),
        ...(search.managerId === undefined ? {} : { managerId: search.managerId }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const managers = useQuery({ queryKey: ['managers'], queryFn: () => managersApi.list() });

  const tenants = query.data?.pages.flatMap((page) => page.tenants) ?? [];

  function submitSearch(value: string): void {
    const q = value.trim();
    if (q === (search.q ?? '')) return;
    void navigate({ search: (prev) => ({ ...prev, q: q === '' ? undefined : q }) });
  }

  return (
    <Page title="Клиники" description="Подписки клиник и лабораторий платформы.">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch(qDraft);
        }}
      >
        <div className="w-full sm:w-72">
          <TextInput
            size="m"
            placeholder="Название или почта владельца"
            value={qDraft}
            onUpdate={setQDraft}
            onBlur={() => {
              submitSearch(qDraft);
            }}
            hasClear
            startContent={<Icon data={Magnifier} size={16} className="ml-2 text-[var(--g-color-text-secondary)]" />}
            controlProps={{ 'aria-label': 'Поиск клиники' }}
          />
        </div>

        <Select
          size="m"
          width={190}
          label="Состояние:"
          value={[search.state ?? ANY]}
          options={[
            { value: ANY, content: 'Любое' },
            ...(Object.keys(STATE_LABEL) as SubscriptionState[]).map((value) => ({ value, content: STATE_LABEL[value] })),
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({
              search: (prev) => ({ ...prev, state: value === ANY ? undefined : (value as SubscriptionState) }),
            });
          }}
        />

        <Select
          size="m"
          width={160}
          label="Тип:"
          value={[search.kind ?? ANY]}
          options={[
            { value: ANY, content: 'Любой' },
            { value: 'clinic', content: 'Клиника' },
            { value: 'lab', content: 'Лаборатория' },
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({
              search: (prev) => ({ ...prev, kind: value === ANY ? undefined : (value as 'clinic' | 'lab') }),
            });
          }}
        />

        <Select
          size="m"
          width={230}
          label="Менеджер:"
          filterable={(managers.data?.length ?? 0) > 8}
          value={[search.managerId ?? ANY]}
          options={[
            { value: ANY, content: 'Любой' },
            { value: 'none', content: 'Без менеджера' },
            ...(managers.data ?? []).map((manager) => ({ value: manager.id, content: manager.fullName })),
          ]}
          onUpdate={(values) => {
            const value = values[0];
            void navigate({ search: (prev) => ({ ...prev, managerId: value === ANY ? undefined : value }) });
          }}
        />
      </form>

      {query.isError ? (
        <ErrorNote title="Список не загрузился" error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <LoadingRows rows={8} />
      ) : tenants.length === 0 ? (
        <Empty
          title="По этому фильтру клиник не найдено"
          description="Измените отбор или поищите по названию и почте владельца."
        />
      ) : (
        <>
          <div className="hidden rounded-xl border border-[var(--g-color-line-generic)] md:block">
            <Table
              className="w-full"
              edgePadding
              stickyHorizontalScroll
              data={tenants}
              getRowDescriptor={(row) => ({ id: row.id, interactive: true })}
              onRowClick={(row, _index, event) => {
                // Щелчок по ссылке внутри строки ведёт сам; строка — запасная цель.
                if ((event.target as HTMLElement).closest('a') !== null) return;
                go(`/tenants/${row.id}`);
              }}
              columns={[
                {
                  id: 'name',
                  name: 'Название',
                  primary: true,
                  template: (row) => <RouterLink to={`/tenants/${row.id}`}>{row.name}</RouterLink>,
                },
                { id: 'kind', name: 'Тип', template: (row) => KIND_LABEL[row.kind] },
                { id: 'owner', name: 'Владелец', template: (row) => <OwnerCell row={row} /> },
                {
                  id: 'state',
                  name: 'Подписка',
                  template: (row) => (
                    <Label theme={STATE_TONE[row.subscriptionState]}>{STATE_LABEL[row.subscriptionState]}</Label>
                  ),
                },
                {
                  id: 'paid',
                  name: 'Оплачено до',
                  template: (row) => (
                    <span className="tabular">{row.currentPeriodEnd === null ? '—' : day(row.currentPeriodEnd)}</span>
                  ),
                },
                {
                  id: 'manager',
                  name: 'Менеджер',
                  template: (row) =>
                    row.manager === null ? (
                      <Text color="secondary">—</Text>
                    ) : (
                      <RouterLink to={`/managers/${row.manager.id}`} view="secondary">
                        {row.manager.fullName}
                      </RouterLink>
                    ),
                },
                {
                  id: 'created',
                  name: 'Регистрация',
                  template: (row) => <span className="tabular">{day(row.createdAt)}</span>,
                },
              ]}
            />
          </div>

          <ul className="flex flex-col gap-2 md:hidden">
            {tenants.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="flex w-full cursor-pointer flex-col gap-2 rounded-xl border border-[var(--g-color-line-generic)] bg-[var(--g-color-base-background)] p-3 text-left active:bg-[var(--g-color-base-simple-hover)]"
                  onClick={() => {
                    go(`/tenants/${row.id}`);
                  }}
                >
                  <div className="flex w-full items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col">
                      <Text variant="subheader-1" ellipsis>
                        {row.name}
                      </Text>
                      <Text variant="caption-2" color="secondary">
                        {KIND_LABEL[row.kind]}
                        {row.manager === null ? '' : ` · ${row.manager.fullName}`}
                      </Text>
                    </div>
                    <Label theme={STATE_TONE[row.subscriptionState]}>{STATE_LABEL[row.subscriptionState]}</Label>
                  </div>
                  <OwnerCell row={row} />
                  <Text variant="caption-2" color="secondary" className="tabular">
                    Регистрация: {day(row.createdAt)}
                    {row.currentPeriodEnd === null ? '' : ` · оплачено до ${day(row.currentPeriodEnd)}`}
                  </Text>
                </button>
              </li>
            ))}
          </ul>

          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button view="outlined" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                Показать ещё
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Page>
  );
}

function OwnerCell({ row }: { row: AdminTenantSummary }) {
  if (row.ownerEmail === null && row.ownerName === null) {
    return <Text color="secondary">Не назначен</Text>;
  }

  return (
    <div className="flex flex-col">
      {row.ownerName === null ? null : <Text>{row.ownerName}</Text>}
      {row.ownerEmail === null ? null : (
        <Text variant="caption-2" color="secondary">
          {row.ownerEmail}
        </Text>
      )}
    </div>
  );
}
